/**
 * 代理ログインの受け口（**認証不要**の着地ページ）。
 *
 * 運営コンソールの「契約者の画面を開く」は
 * `POST /api/v1/ops/orgs/:orgId/impersonate` の応答にある
 * `portalUrl`（= `${PORTAL_ORIGIN}/impersonate#access_token=...`）を
 * そのまま `window.open()` する。**そのパスが SPA に無く 404 に落ちていた。**
 * refresh Cookie は同時に立つので手で URL を削れば入れてしまうが、
 * 運営者から見える結果は「開いたら見つかりませんでした」だけだった。
 *
 * 守っていること:
 *   1. **トークンはフラグメントからだけ読む。** クエリに載せない決まりは
 *      サーバ側（`routes/ops/orgs.ts`）の判断で、Referer・アクセスログ・
 *      ブラウザ履歴に残さないため。読んだら即
 *      `history.replaceState()` で URL から消す（履歴に残さない）。
 *   2. **access token をストレージに置かない。** `setAccessToken()` は
 *      メモリのみ（`lib/api.ts` の方針）。リロード後は refresh Cookie から
 *      `bootstrapSession()` で復帰する。
 *   3. トークンが無い／不正なら `/login` へ落とす。ここで独自のログイン
 *      フォームを出すと、代理ログインの導線とふつうのログインが混ざる。
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Alert, AlertDescription, AlertTitle, Button } from '@mail-sv/ui';
import { Loader2, ShieldAlert } from 'lucide-react';
import { setAccessToken } from '../lib/api.js';
import { fetchMe } from '../lib/auth.js';
import { authStore } from '../stores/auth.js';
import { useAppNavigate } from '../lib/nav.js';

/** `#access_token=...&foo=bar` からトークンを取り出す。 */
export function readTokenFromHash(hash: string): string {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw.length === 0) return '';
  return new URLSearchParams(raw).get('access_token') ?? '';
}

export function ImpersonatePage() {
  const navigate = useAppNavigate();
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    const token = readTokenFromHash(window.location.hash);
    /*
     * **読んだ直後にフラグメントを消す。** 消さないと、この後の
     * `navigate()` でパスだけ変わってフラグメントが残り、
     * 運営者がその URL をコピーして共有できてしまう。
     */
    if (token.length > 0) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (token.length === 0) {
      setFailed(true);
      return;
    }

    setAccessToken(token);
    let cancelled = false;
    void (async () => {
      try {
        const me = await fetchMe();
        if (cancelled) return;
        authStore.setUser(me);
        navigate({ to: '/', replace: true });
      } catch {
        if (cancelled) return;
        // トークンが期限切れ／失効している。代理セッションを取り直すのは
        // 運営コンソール側の操作なので、ここでは案内だけ出す。
        setAccessToken(null);
        authStore.setAnonymous();
        setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-4">
        {failed ? (
          <>
            <Alert variant="destructive">
              <AlertTitle className="flex items-center gap-2">
                <ShieldAlert className="size-4" aria-hidden />
                代理ログインを開始できませんでした
              </AlertTitle>
              <AlertDescription>
                リンクの有効期限が切れているか、代理セッションが終了しています。
                運営コンソールから代理ログインをやり直してください。
              </AlertDescription>
            </Alert>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">ログイン画面へ</Link>
            </Button>
          </>
        ) : (
          <div
            className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
            aria-busy="true"
            aria-live="polite"
          >
            <Loader2 className="size-4 animate-spin" aria-hidden />
            契約者の画面を開いています…
          </div>
        )}
      </div>
    </div>
  );
}
